import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Building2, Check, X } from "lucide-react";
import { RaynetAddress } from '@/api/types';
import { useTranslation } from 'react-i18next';

interface AddressSelectorProps {
  addresses: RaynetAddress[];
  onSelect: (address: RaynetAddress) => void;
  onCancel: () => void;
  selectedAddressId?: number;
}

export const AddressSelector: React.FC<AddressSelectorProps> = ({
  addresses,
  onSelect,
  onCancel,
  selectedAddressId
}) => {
  const { t } = useTranslation('common');
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  if (addresses.length === 0) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <MapPin className="text-amber-600" size={20} />
            <div>
              <p className="text-amber-800 text-sm font-medium">
                {t('ui.address_selector.no_addresses')}
              </p>
              <p className="text-amber-700 text-sm">
                {t('ui.address_selector.no_addresses_description')}
              </p>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button 
              onClick={onCancel}
              variant="outline" 
              size="sm"
              className="text-amber-700 border-amber-300 hover:bg-amber-100"
            >
              <X size={14} className="mr-1" />
              {t('buttons.close')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <Building2 className="text-orange-600" size={20} />
          <div>
            <p className="text-orange-800 text-sm font-medium">
              {t('ui.address_selector.title')}
            </p>
            <p className="text-orange-700 text-sm">
              {t('ui.address_selector.description')}
            </p>
          </div>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                selectedAddressId === address.id
                  ? 'border-green-400 bg-green-50'
                  : hoveredId === address.id
                  ? 'border-orange-300 bg-orange-100'
                  : 'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50'
              }`}
              onClick={() => onSelect(address)}
              onMouseEnter={() => setHoveredId(address.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-slate-800 text-sm">
                      {address.name}
                    </h4>
                    <div className="flex gap-1">
                      {address.isPrimary && (
                        <Badge variant="default" className="text-xs bg-green-600">
                          {t('ui.address_selector.primary')}
                        </Badge>
                      )}
                      {address.isContactAddress && (
                        <Badge variant="secondary" className="text-xs">
                          {t('ui.address_selector.contact')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm text-slate-600">
                    <p>{address.street}</p>
                    <p>{address.zipCode} {address.city}</p>
                    {address.province && (
                      <p className="text-xs text-slate-500">{address.province}, {address.country}</p>
                    )}
                  </div>
                </div>

                {selectedAddressId === address.id && (
                  <div className="ml-2">
                    <div className="bg-green-500 rounded-full p-1">
                      <Check size={12} className="text-white" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button 
            onClick={onCancel}
            variant="outline" 
            size="sm"
            className="text-slate-600 border-slate-300 hover:bg-slate-100"
          >
            <X size={14} className="mr-1" />
            {t('buttons.cancel')}
          </Button>
          {selectedAddressId && (
            <Button 
              onClick={() => {
                const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
                if (selectedAddress) {
                  onSelect(selectedAddress);
                }
              }}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Check size={14} className="mr-1" />
              {t('ui.address_selector.use_selected')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}; 